// @ts-check
import { paintQuantumIsland, paintQuantumReturn } from './quantum.js';
import { paintBandwidthCore, paintSecretDoors } from './bandwidth-core.js';
import { paintEnemy, paintBlock, paintPickup, paintSign, paintTransistor } from './machines.js';
import { POWER } from '../game/tuning.js';
import { UPLINK } from '../game/uplink.js';
import { querySolids } from "../game/geometry.js";
import { paintDevices, paintBoss } from './devices.js';
/** @typedef {import('../game/world.js').Level} Level */
/** @typedef {{void: string, panel: string, edge: string, cyan: string, violet: string, danger: string, light: string, metal:string, steel:string, gold:string}} Palette */

/** @param {CanvasRenderingContext2D} ctx @param {number} x @param {number} y @param {number} w @param {number} h @param {string} fill @param {string} edge */
function panel(ctx, x, y, w, h, fill, edge) {
  ctx.fillStyle = fill; ctx.fillRect(x, y, w, h);
  ctx.strokeStyle = edge; ctx.lineWidth = 1;
  ctx.strokeRect(x + 0.5, y + 0.5, w - 1, h - 1);
}

/** Draw foreground geometry in the exact coordinate system used by collisions.
 * @param {CanvasRenderingContext2D} ctx @param {Level} level @param {number} camera
 * @param {{width:number,height:number,floor:number,scale:number}} view @param {Palette} colors @param {boolean} reduced @param {{x:number,y:number,age:number,broken:boolean}[]} [sparks]
 */
export function paintLevel(ctx, level, camera, view, colors, reduced, sparks = []) {
  const { width, height, floor, scale } = view;
  const visibleWidth = width / scale;
  ctx.clearRect(0, 0, width, height);
  ctx.save(); ctx.translate(-camera * scale, floor); ctx.scale(scale, scale);
  const start = Math.max(0, Math.floor(camera / 32) - 1);
  const end = Math.min(level.width / 32, Math.ceil((camera + visibleWidth) / 32) + 1);
  const depth = Math.max(64, (height - floor) / scale);
  // Shafts have a recessed back wall, receding rails, and exposed cut edges.
  // QP is drawn above this Canvas and remains visible below the ground plane.
  for (const [left, right] of level.gaps) {
    if (right < camera || left > camera + visibleWidth) continue;
    const well = ctx.createLinearGradient(0, 0, 0, depth);
    well.addColorStop(0, colors.metal); well.addColorStop(0.2, colors.panel); well.addColorStop(1, colors.void);
    ctx.fillStyle = well; ctx.fillRect(left, 0, right - left, depth);
    const center = (left + right) / 2;
    ctx.strokeStyle = colors.edge; ctx.globalAlpha = 0.45; ctx.lineWidth = 1;
    for (const edge of [left + 5, right - 5]) {
      ctx.beginPath(); ctx.moveTo(edge, 0); ctx.lineTo(center + (edge - center) * 0.35, depth); ctx.stroke();
    }
    for (let row = 12; row < depth; row += 18) {
      const inset = Math.min((right - left) * 0.24, row * 0.08);
      ctx.beginPath(); ctx.moveTo(left + inset, row); ctx.lineTo(right - inset, row); ctx.stroke();
    }
    ctx.globalAlpha = 1;
    for (const edge of [left, right - 6]) {
      ctx.fillStyle = colors.metal; ctx.fillRect(edge, 0, 6, depth);
      ctx.fillStyle = colors.steel; ctx.fillRect(edge, 0, 6, 3);
      for (let y = 10; y < depth; y += 24) {
        ctx.globalAlpha = Math.max(0.08, 0.55 * (1 - y / depth));
        ctx.fillStyle = colors.danger; ctx.fillRect(edge + 2, y, 2, 6);
      }
    }
    ctx.globalAlpha = 1;
  }
  for (let col = start; col < end; col++) {
    if (level.gaps.some(([left, right]) => col * 32 >= left && col * 32 < right)) continue;
    if (level.definition.quantum) {
      ctx.fillStyle = colors.void; ctx.fillRect(col * 32, 0, 32, depth);
      ctx.fillStyle = colors.violet; ctx.fillRect(col * 32, 0, 32, 3);
      ctx.globalAlpha = .45; ctx.strokeStyle = colors.cyan; ctx.lineWidth = 1;
      ctx.fillStyle = colors.violet; ctx.beginPath(); ctx.moveTo(col * 32, 4); ctx.lineTo(col * 32 + 32, 4); ctx.lineTo(col * 32 + 18, 30 + col % 5 * 11); ctx.closePath(); ctx.fill(); ctx.globalAlpha = 1;
      continue;
    }
    panel(ctx, col * 32, 0, 32, depth, colors.panel, colors.edge);
    ctx.fillStyle = colors.cyan; ctx.fillRect(col * 32, 0, 32, 2);
    ctx.globalAlpha = 0.4; ctx.fillRect(col * 32 + 5, 8, 14, 2); ctx.globalAlpha = 1;
    ctx.strokeStyle = colors.edge;
    for (let y = 22; y < depth; y += 32) {
      ctx.strokeRect(col * 32 + 4, y, 24, 22);
      ctx.fillStyle = colors.edge; ctx.fillRect(col * 32 + 8, y + 5, 3, 3);
    }
  }
  for (const solid of querySolids(level, camera - 32, camera + visibleWidth + 32)) {
    if (solid.mechanism) continue;
    if (!solid.active || solid.x + solid.width < camera - 32 || solid.x > camera + visibleWidth + 32) continue;
    if (solid.hidden && !solid.used) {
      continue;
    }
    const { x, width: w, height: h } = solid;
    const y = -solid.y - h - (reduced ? 0 : Math.sin(solid.bump / 0.16 * Math.PI) * 5);
    if (solid.ceiling) {
      ctx.fillStyle = colors.void; ctx.fillRect(x, y, w, h);
      const skin = ctx.createLinearGradient(0, -solid.y - 14, 0, -solid.y + 7);
      skin.addColorStop(0, colors.void); skin.addColorStop(.65, colors.panel); skin.addColorStop(1, colors.violet);
      ctx.fillStyle = skin; ctx.fillRect(x, -solid.y - 14, w, 14);
      ctx.globalAlpha = .6; ctx.strokeStyle = colors.violet; ctx.lineWidth = 1;
      ctx.beginPath(); ctx.moveTo(x, -solid.y); ctx.lineTo(x + w, -solid.y); ctx.stroke(); ctx.globalAlpha = 1;
    } else if (level.definition.quantum) {
      paintQuantumIsland(ctx, solid, y, colors);
    } else if (solid.kind === 'conduit') {
      panel(ctx, x + 4, y + 12, w - 8, h - 12, colors.panel, colors.cyan);
      const gradient = ctx.createLinearGradient(x, 0, x + w, 0);
      gradient.addColorStop(0, colors.panel); gradient.addColorStop(0.35, colors.edge); gradient.addColorStop(0.48, colors.panel); gradient.addColorStop(1, colors.void);
      ctx.fillStyle = gradient; ctx.fillRect(x + 5, y + 14, w - 10, h - 15);
      ctx.strokeStyle = colors.cyan; ctx.globalAlpha = 0.65;
      ctx.beginPath(); ctx.moveTo(x + 14, y + 18); ctx.lineTo(x + 14, y + h - 8); ctx.moveTo(x + w - 14, y + 18); ctx.lineTo(x + w - 14, y + h - 8); ctx.stroke(); ctx.globalAlpha = 1;
      panel(ctx, x, y, w, 15, colors.panel, colors.cyan);
      ctx.fillStyle = colors.cyan; ctx.fillRect(x + 3, y + 2, w - 6, 2);
      for (let i = 0; i < 3; i++) ctx.fillRect(x + 22 + i * 7, y + 7, 3, 3);
    } else {
      paintBlock(ctx, solid, y, colors);
      if (solid.sign) paintSign(ctx, solid, y, colors);
    }
  }
  for (const [index, item] of (level.definition.transistors ?? []).entries()) {
    if (item.x < camera - 24 || item.x > camera + visibleWidth + 24) continue;
    paintTransistor(ctx, item.x, item.y, !!(level.transistorsFound & (1 << (item.slot ?? index))), colors);
  }
  paintSecretDoors(ctx, level, colors, reduced);
  paintDevices(ctx, level, camera - 160, camera + visibleWidth + 160, colors, reduced);
  if (level.boss && level.boss.x + level.boss.width > camera - 160 && level.boss.x < camera + visibleWidth + 160) paintBoss(ctx, level.boss, colors, reduced);
  for (const item of level.pickups) if (item.x + item.width >= camera - 32 && item.x <= camera + visibleWidth + 32) {
    ctx.save();
    if (item.kind !== 'bit' && item.age < POWER.emergeTime) {
      ctx.beginPath(); ctx.rect(item.x - 16, -item.originY - 64, item.width + 32, 64); ctx.clip();
    }
    paintPickup(ctx, item, colors, reduced); ctx.restore();
  }
  for (const enemy of level.enemies) {
    if ((enemy.mode === 'dead' && !enemy.dead) || enemy.x + enemy.width < camera - 32 || enemy.x > camera + visibleWidth + 32) continue;
    paintEnemy(ctx, enemy, level.time, colors, reduced);
  }
  ctx.fillStyle = colors.violet;
  for (const shot of level.shots) {
    ctx.shadowColor = colors.violet; ctx.shadowBlur = reduced ? 0 : 8;
    ctx.fillRect(shot.x, -shot.y - shot.height, shot.width, shot.height);
    ctx.fillStyle = colors.light; ctx.fillRect(shot.x + 2, -shot.y - shot.height + 1, 4, 2); ctx.fillStyle = colors.violet;
  }
  ctx.shadowBlur = 0;
  if (!reduced) for (const spark of sparks) {
    ctx.fillStyle = spark.broken ? colors.cyan : colors.violet; ctx.globalAlpha = 1 - spark.age * 2;
    for (let i = 0; i < 5; i++) {
      const dx = (i - 2) * spark.age * 65;
      const dy = spark.age * (120 + i % 2 * 70) - 240 * spark.age * spark.age;
      ctx.fillRect(spark.x + dx - 2, -spark.y - dy, spark.broken ? 6 : 3, spark.broken ? 6 : 3);
    }
    ctx.globalAlpha = 1;
  }
  if (level.definition.bandwidthGoal && level.finish > camera - 250 && level.finish < camera + visibleWidth + 250) paintBandwidthCore(ctx, level, colors, reduced);
  if (level.definition.quantum && !level.definition.quantumFinale) {
    if (level.finish > camera - 180 && level.finish < camera + visibleWidth + 180)
      paintQuantumReturn(ctx, level.finish + 12, level.time, colors, reduced, level.status === 'won');
    ctx.restore(); return;
  }
  // An electrical uplink port terminates the route inside the computer.
  const gate = level.finish + (level.definition.bandwidthGoal ? UPLINK.victoryOffset : UPLINK.gateOffset);
  if (gate > camera - 120 && gate < camera + visibleWidth + 120) {
    const base = -querySolids(level, gate, gate).filter((solid) => solid.active && !solid.ceiling).reduce((top, solid) => Math.max(top, solid.y + solid.height), 0);
    panel(ctx, gate - 48, base - 144, 16, 144, colors.panel, colors.cyan);
    panel(ctx, gate + 32, base - 144, 16, 144, colors.panel, colors.cyan);
    panel(ctx, gate - 48, base - 160, 96, 20, colors.panel, colors.cyan);
    ctx.fillStyle = colors.cyan; ctx.globalAlpha = level.status === 'won' ? 0.45 : 0.1;
    ctx.fillRect(gate - 32, base - 140, 64, 140); ctx.globalAlpha = 1;
    for (let y = 0; y < 6; y++) {
      ctx.fillStyle = y % 2 ? colors.violet : colors.cyan;
      ctx.fillRect(gate - 43, base - 130 + y * 22, 6, 8);
      ctx.fillRect(gate + 37, base - 130 + y * 22, 6, 8);
    }
    ctx.strokeStyle = colors.cyan;
    const scan = reduced ? 70 : level.time * 45 % 132;
    ctx.beginPath(); ctx.moveTo(gate - 30, base - scan); ctx.lineTo(gate + 30, base - scan); ctx.stroke();
    ctx.fillStyle = colors.light; ctx.font = '9px monospace'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle'; ctx.fillText(level.boss && level.boss.phase !== 'defeated' ? 'LOCKED' : 'UPLINK', gate, base - 150);
  }
  ctx.restore();
}

/** @param {HTMLCanvasElement} canvas */
export function createLevelRenderer(canvas) {
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Level Canvas is unavailable');
  const style = window.getComputedStyle(document.documentElement);
  const color = (/** @type {string} */ name) => style.getPropertyValue(`--color-${name}`).trim();
  const colors = { void: color('space-void'), panel: color('space-floor'), edge: color('space-wire'), cyan: color('glow-cyan'), violet: color('space-violet'), danger: color('danger'), light: color('surface-12'), metal: color('metal'), steel: color('steel'), gold: color('reward-gold') };
  return {
    /** @param {Level} level @param {number} camera @param {{width:number,height:number,floor:number,scale:number}} view @param {boolean} reduced @param {{x:number,y:number,age:number,broken:boolean}[]} [sparks] */
    render(level, camera, view, reduced, sparks = []) {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      if (canvas.width !== Math.round(view.width * dpr) || canvas.height !== Math.round(view.height * dpr)) {
        canvas.width = Math.round(view.width * dpr); canvas.height = Math.round(view.height * dpr);
      }
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      paintLevel(ctx, level, camera, view, colors, reduced, sparks);
    },
  };
}
